import React from "react";
import { X } from "lucide-react";
import { cn, useUiStore } from "@kkp/app-kit";
import { Button } from "@kkp/ui";
import { NavLink } from "@kkp/ui";
import { Logo } from "../branding/Logo";

export type SidebarNavItem = {
  label: string;
  to: string;
};

export type SidebarProps = {
  /**
   * Current pathname (from react-router location.pathname), e.g. "/clients"
   * Used for active state highlighting.
   */
  pathname: string;
  /**
   * Base hash prefix for links. For HashRouter, keep "#".
   * Links will be rendered as `${hashPrefix}${to}` (e.g. "#/clients").
   */
  hashPrefix?: string;
  /**
   * App / brand title shown in the sidebar.
   */
  title?: string;
  /**
   * Optional subtitle (small muted line).
   */
  subtitle?: string;
  /**
   * Optional nav items; defaults to BLUEPRINT navigation.
   */
  items?: SidebarNavItem[];
  /**
   * User identity area (optional).
   */
  user?: {
    name: string;
    role?: string;
  } | null;
  /**
   * Called when user presses "Logout" in the sidebar footer.
   */
  onLogout?: () => void;
  /**
   * Controlled mobile open state (drawer). If undefined, defaults to closed.
   */
  mobileOpen?: boolean;
  /**
   * Change handler for mobile open state.
   */
  onMobileOpenChange?: (open: boolean) => void;
  /**
   * Optional custom className.
   */
  className?: string;
};

const DEFAULT_ITEMS: SidebarNavItem[] = [
  { label: "Company", to: "/company" },
  { label: "FDS", to: "/fds" },
  { label: "Logs", to: "/logs" },
  ...(__SANDBOX_BUILD__ ? [{ label: "Sandbox", to: "/sandbox" }] : []),
];

/**
 * Sidebar navigation for the dashboard shell.
 *
 * Design constraints:
 * - Uses existing brand tokens from `brand-base.css` (`--kkp-*`)
 * - Matches the login page aesthetic: dark glassy surfaces, subtle borders
 * - Uses HashRouter-friendly links (`href="#/route"`)
 */
export function Sidebar({
  pathname,
  hashPrefix = "#",
  title: _title = "KKP-Admin",
  subtitle: _subtitle = "KamuKirim - Payroll",
  items = DEFAULT_ITEMS,
  user: _user,
  onLogout,
  mobileOpen,
  onMobileOpenChange,
  className,
}: SidebarProps) {
  const { sidebarCollapsed, toggleSidebar: _toggleSidebar } = useUiStore();
  const [internalMobileOpen, setInternalMobileOpen] = React.useState(false);
  const isMobileOpen = mobileOpen ?? internalMobileOpen;
  const setMobileOpen = React.useCallback(
    (open: boolean) => {
      if (onMobileOpenChange) {
        onMobileOpenChange(open);
      } else {
        setInternalMobileOpen(open);
      }
    },
    [onMobileOpenChange],
  );

  return (
    <>
      {isMobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[6px] md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <aside
        className={cn(
          // Mobile: overlay drawer, doesn't participate in layout (prevents squeezing content)
          "fixed inset-y-0 left-0 z-50 flex flex-col gap-3 overflow-y-auto",
          // Keep it within viewport width on small screens
          "w-[280px] max-w-full",
          "-translate-x-[120%] rounded-[var(--kkp-radius)] transition-transform duration-200 ease-out max-h-screen",
          isMobileOpen && "translate-x-0",
          // Desktop: static sidebar in the flex row
          sidebarCollapsed ? "md:w-[112px]" : "md:w-[260px]",
          "md:static md:translate-x-0 md:flex md:max-h-none md:overflow-visible",
          // Use brand surfaces (consistent with the rest of the app)
          "border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] backdrop-blur-[10px]",
          "p-3 pt-14 shadow-[var(--kkp-shadow-strong)] md:pt-3 md:shadow-none",
          "md:h-auto",
          className,
        )}
        aria-label="Sidebar"
      >
        <Button
          variant="ghost"
          size="sm"
          aria-label="Close menu"
          className="md:hidden absolute right-3 top-3 z-10 h-10 w-10 p-0 rounded-full border-2 border-[color:var(--kkp-border-strong)] ring-1 ring-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] hover:bg-[color:var(--kkp-surface-2)] shadow-[0_10px_20px_rgba(0,0,0,0.3)]"
          onClick={() => setMobileOpen(false)}
        >
          <X className="h-5 w-5" />
        </Button>

        <div className="relative flex items-center gap-2 px-2 py-2">
          <Logo
            height={sidebarCollapsed ? 48 : 60}
            alt="KamuKirim - Payroll"
            className="max-w-[220px] shrink-0 mx-auto md:mx-0"
          />
        </div>

        <nav className="grid gap-1 px-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              label={item.label}
              to={item.to}
              pathname={pathname}
              hashPrefix={hashPrefix}
              className={sidebarCollapsed ? "justify-center" : undefined}
              onClick={() => setMobileOpen(false)}
            />
          ))}
        </nav>

        <div className="mt-auto border-t border-[color:var(--kkp-border)] pt-3 px-2">
          <Button
            variant="primary"
            size="md"
            fullWidth
            className="mt-3"
            onClick={onLogout}
            disabled={!onLogout}
            title={onLogout ? "Logout" : "Logout handler not wired"}
          >
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
}
